import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const formatNumber = (n) => {
  const num = Number(n || 0);
  return new Intl.NumberFormat("en-IN").format(num);
};

const monthName = (month) => {
  const m = Number(month);
  if (!m || m < 1 || m > 12) return "-";
  return new Date(2000, m - 1, 1).toLocaleString("en-IN", { month: "long" });
};

const formatPeriod = (month, year) => {
  const y = year != null ? String(year) : "";
  const mn = monthName(month);
  return y ? `${mn} ${y}` : mn;
};

const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return String(iso);
  }
};

const statusBadgeStyle = (kind, raw) => {
  const s = String(raw || "").toLowerCase();
  if (kind === "payment") {
    if (s === "paid" || s === "completed" || s === "success")
      return { bg: "rgba(34,197,94,0.18)", color: "#16a34a" };
    if (s === "pending" || s === "due")
      return { bg: "rgba(234,179,8,0.2)", color: "#a16207" };
    if (s === "failed" || s === "overdue")
      return { bg: "rgba(239,68,68,0.18)", color: "#dc2626" };
  }
  if (kind === "record") {
    if (s === "active") return { bg: "rgba(34,197,94,0.18)", color: "#16a34a" };
    if (s === "inactive" || s === "cancelled")
      return { bg: "rgba(107,114,128,0.2)", color: "#4b5563" };
  }
  return { bg: "rgba(59,130,246,0.15)", color: "#2563eb" };
};

const isPaymentPending = (row) =>
  String(row?.paymentStatus || "").toLowerCase().trim() === "pending";

function getTenantIdForRow(row, fallbackUserId) {
  const raw = row?.tenantId;
  if (raw != null && typeof raw === "object") {
    return raw._id || raw.id || "";
  }
  return raw != null ? String(raw) : fallbackUserId;
}

function getTenantLabel(row) {
  const t = row?.tenantId;
  if (t && typeof t === "object") {
    const name = t.name || "";
    const email = t.email || "";
    if (name && email) return `${name} · ${email}`;
    return name || email || "—";
  }
  return "—";
}

/**
 * Lambda returns: { status, message, upload: { uploadUrl, key, bucket, ... } }
 * Normalize to a flat object with uploadUrl + key for the rest of the flow.
 */
function unwrapPresignPayload(d) {
  if (!d || typeof d !== "object") return null;

  const nested =
    d.upload && typeof d.upload === "object"
      ? d.upload
      : d.data?.upload && typeof d.data.upload === "object"
        ? d.data.upload
        : null;

  if (nested?.uploadUrl && nested?.key) {
    return { ...d, ...nested };
  }
  if (d.uploadUrl && d.key) return d;
  if (d.data && typeof d.data === "object" && d.data.uploadUrl && d.data.key) {
    return { ...d, ...d.data };
  }
  return d;
}

const Payment = () => {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const userRole = (localStorage.getItem("role") || "").toUpperCase();
  const isAdmin = userRole === "ADMIN";

  const userId = useMemo(
    () =>
      localStorage.getItem("userid") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("id") ||
      "",
    []
  );

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: isAdmin ? 20 : 10,
    total: 0,
    pages: 1
  });

  const [adminSummary, setAdminSummary] = useState(null);
  const [adminSearchInput, setAdminSearchInput] = useState("");
  const [adminSearch, setAdminSearch] = useState("");
  const [adminPaymentStatus, setAdminPaymentStatus] = useState("");
  const [adminYear, setAdminYear] = useState("");
  const [adminMonth, setAdminMonth] = useState("");

  const [payModalRow, setPayModalRow] = useState(null);
  const [proofNotes, setProofNotes] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [proofPreviewUrl, setProofPreviewUrl] = useState(null);
  const [submittingProof, setSubmittingProof] = useState(false);
  const proofPreviewObjectUrlRef = useRef(null);

  const closePayModal = useCallback(() => {
    if (proofPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(proofPreviewObjectUrlRef.current);
      proofPreviewObjectUrlRef.current = null;
    }
    setPayModalRow(null);
    setProofNotes("");
    setProofFile(null);
    setProofPreviewUrl(null);
  }, []);

  const fetchPayments = useCallback(
    async (page = 1, limit = 10, overrides = {}) => {
      if (!API_BASE_URL) {
        toast.error("Missing REACT_APP_API_URL in environment");
        return;
      }
      if (!userId) {
        toast.error("Missing user id. Please login again.");
        return;
      }

      setLoading(true);
      let didIncrement = false;
      try {
        loaderIncrement();
        didIncrement = true;

        let payload;
        if (isAdmin) {
          const searchUsed =
            overrides.adminSearch !== undefined ? overrides.adminSearch : adminSearch;
          payload = {
            action: "admin.getTenantPaymentHistory",
            userId,
            page,
            limit,
            includeDownloadUrls: true,
            expiresIn: 7200
          };
          const q = String(searchUsed || "").trim();
          if (q) payload.search = q;
          if (adminPaymentStatus) payload.paymentStatus = adminPaymentStatus;
          const y = adminYear === "" ? NaN : Number(adminYear);
          const mo = adminMonth === "" ? NaN : Number(adminMonth);
          if (Number.isFinite(y)) payload.year = y;
          if (Number.isFinite(mo) && mo >= 1 && mo <= 12) payload.month = mo;
        } else {
          payload = {
            action: "tenant.getPaymentHistory",
            userId,
            page,
            limit
          };
        }

        const response = await axios.post(`${API_BASE_URL}`, payload);
        const data = response?.data;

        if (data?.status === "success" && Array.isArray(data?.payments)) {
          setPayments(data.payments);
          setPagination(
            data.pagination || {
              page,
              limit,
              total: data.payments.length,
              pages: 1
            }
          );
          if (isAdmin) {
            setAdminSummary(data.summary || null);
          } else {
            setAdminSummary(null);
          }
        } else {
          toast.error(data?.message || "Failed to load payment history");
          setPayments([]);
          setAdminSummary(null);
        }
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || "Something went wrong";
        toast.error(msg);
        setPayments([]);
        setAdminSummary(null);
      } finally {
        if (didIncrement) loaderDecrement();
        setLoading(false);
      }
    },
    [
      API_BASE_URL,
      userId,
      isAdmin,
      adminSearch,
      adminPaymentStatus,
      adminYear,
      adminMonth
    ]
  );

  useEffect(() => {
    fetchPayments(pagination.page, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyAdminFilters = (e) => {
    e?.preventDefault?.();
    const q = adminSearchInput.trim();
    setAdminSearch(q);
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchPayments(1, pagination.limit, { adminSearch: q });
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    fetchPayments(newPage, pagination.limit);
  };

  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({ ...prev, page: 1, limit: newLimit }));
    fetchPayments(1, newLimit);
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear + 1 - i);

  const handleView = useCallback(
    (row) => {
      const tid = getTenantIdForRow(row, userId);
      if (!tid) {
        toast.error("Cannot open details for this payment.");
        return;
      }
      navigate(`/payment-view/${encodeURIComponent(String(tid))}`);
    },
    [navigate, userId]
  );

  const handlePay = useCallback((row) => {
    if (!isPaymentPending(row)) return;
    if (proofPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(proofPreviewObjectUrlRef.current);
      proofPreviewObjectUrlRef.current = null;
    }
    setProofNotes("");
    setProofFile(null);
    setProofPreviewUrl(null);
    setPayModalRow(row);
  }, []);

  const onProofFileChange = (e) => {
    const file = e.target.files?.[0];
    if (proofPreviewObjectUrlRef.current) {
      URL.revokeObjectURL(proofPreviewObjectUrlRef.current);
      proofPreviewObjectUrlRef.current = null;
    }
    setProofPreviewUrl(null);
    if (!file) {
      setProofFile(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (screenshot).");
      e.target.value = "";
      return;
    }
    const maxBytes = 8 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error("File is too large (max 8 MB).");
      e.target.value = "";
      return;
    }
    const url = URL.createObjectURL(file);
    proofPreviewObjectUrlRef.current = url;
    setProofFile(file);
    setProofPreviewUrl(url);
  };

  const submitRentPaymentProof = async (e) => {
    e.preventDefault();
    if (!API_BASE_URL || !userId) {
      toast.error("Missing configuration or login.");
      return;
    }
    if (!payModalRow?._id) {
      toast.error("Missing payment record.");
      return;
    }
    if (!proofFile) {
      toast.error("Please attach a payment screenshot.");
      return;
    }

    const fileName = proofFile.name || "receipt.jpg";
    const fileContentType =
      proofFile.type && proofFile.type.startsWith("image/")
        ? proofFile.type
        : "image/jpeg";

    setSubmittingProof(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;

      const presignRes = await axios.post(`${API_BASE_URL}`, {
        action: "tenant.getRentPaymentProofUploadUrl",
        userId,
        paymentId: payModalRow._id,
        fileName,
        contentType: fileContentType
      });
      const presignRaw = presignRes?.data;
      if (presignRaw?.status !== "success") {
        toast.error(presignRaw?.message || "Could not get upload URL");
        return;
      }

      const presign = unwrapPresignPayload(presignRaw);
      const uploadUrl = presign?.uploadUrl;
      const s3Key = presign?.key;
      if (!uploadUrl || !s3Key) {
        toast.error("Invalid upload URL response from server (missing uploadUrl/key).");
        return;
      }

      const contentTypeForPut =
        presign.contentType || fileContentType || "image/jpeg";

      // Use fetch — not axios — so no Authorization / baseURL is applied to the S3 URL
      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": contentTypeForPut
        },
        body: proofFile
      });

      if (!putRes.ok) {
        const errText = await putRes.text().catch(() => "");
        throw new Error(
          `S3 upload failed (${putRes.status}). ${errText ? errText.slice(0, 200) : "Check bucket CORS and Content-Type."}`
        );
      }

      const submitPayload = {
        action: "tenant.submitRentPaymentProof",
        userId,
        paymentId: payModalRow._id,
        s3Key,
        fileName,
        contentType: contentTypeForPut
      };
      const trimmedNotes = proofNotes.trim();
      if (trimmedNotes) {
        submitPayload.notes = trimmedNotes;
      }

      const submitRes = await axios.post(`${API_BASE_URL}`, submitPayload);
      const submitData = submitRes?.data;

      if (submitData?.status === "success" && submitData?.payment) {
        toast.success(submitData?.message || "Payment proof uploaded");
        closePayModal();
        await fetchPayments(pagination.page, pagination.limit);
      } else {
        toast.error(submitData?.message || "Could not save payment proof");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message || error?.message || "Something went wrong";
      toast.error(msg);
    } finally {
      if (didIncrement) loaderDecrement();
      setSubmittingProof(false);
    }
  };

  const generatePageNumbers = () => {
    const current = pagination.page;
    const total = pagination.pages;
    const delta = 2;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages = [];
    pages.push(1);

    let start = Math.max(2, current - delta);
    let end = Math.min(total - 1, current + delta);

    if (current <= delta + 2) {
      end = Math.min(total - 1, 5);
    }
    if (current >= total - delta - 1) {
      start = Math.max(2, total - 4);
    }

    if (start > 2) {
      pages.push("...");
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (end < total - 1) {
      pages.push("...");
    }
    if (total > 1) {
      pages.push(total);
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers();
  const totalEntries = pagination.total ?? payments.length;

  return (
    <div className="employee-form tenant-report">
      <div className="content-header">
        <div className="container-fluid">
          <div
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{ gap: 12 }}
          >
            <div>
              <h1 className="m-0 txt_lft">Payment history</h1>
              <p style={{ margin: 0, color: "#6c757d", fontSize: "14px" }}>
                {isAdmin
                  ? "All tenant rent payments — filter, search, and open details"
                  : "View your rent payments and status"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {isAdmin && adminSummary ? (
            <div className="row mb-3" style={{ marginLeft: 0, marginRight: 0 }}>
              <div className="col-6 col-md-3 mb-2">
                <div className="card h-100">
                  <div className="card-body py-3">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Total records
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      {formatNumber(adminSummary.totalRecords)}
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="col-6 col-md-3 mb-2">
                <div className="card h-100">
                  <div className="card-body py-3">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Paid
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      {formatNumber(adminSummary.paidCount)}
                    </div>
                  </div>
                </div>
              </div> */}
              {/* <div className="col-6 col-md-3 mb-2">
                <div className="card h-100">
                  <div className="card-body py-3">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Pending
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      {formatNumber(adminSummary.pendingCount)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3 mb-2">
                <div className="card h-100">
                  <div className="card-body py-3">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Total paid (₹)
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      {formatNumber(adminSummary.totalPaidAmount)}
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
          ) : null}

         

          <div className="card">
            <div className="card-body">
              <div className="role-table-box">
                {isAdmin ? (
                  <form
                    className="role-table-top mb-3"
                    onSubmit={applyAdminFilters}
                    style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}
                  >
                    <div className="form-group mb-0" style={{ minWidth: 160, flex: "1 1 160px" }}>
                      <label className="form-label" style={{ fontSize: 12 }}>
                        Search
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Tenant name / email…"
                        value={adminSearchInput}
                        onChange={(e) => setAdminSearchInput(e.target.value)}
                      />
                    </div>
                    <div className="form-group mb-0" style={{ width: 130 }}>
                      <label className="form-label" style={{ fontSize: 12 }}>
                        Status
                      </label>
                      <select
                        className="form-control"
                        value={adminPaymentStatus}
                        onChange={(e) => setAdminPaymentStatus(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="paid">Paid</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                    <div className="form-group mb-0" style={{ width: 110 }}>
                      <label className="form-label" style={{ fontSize: 12 }}>
                        Year
                      </label>
                      <select
                        className="form-control"
                        value={adminYear}
                        onChange={(e) => setAdminYear(e.target.value)}
                      >
                        <option value="">Any</option>
                        {yearOptions.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group mb-0" style={{ width: 120 }}>
                      <label className="form-label" style={{ fontSize: 12 }}>
                        Month
                      </label>
                      <select
                        className="form-control"
                        value={adminMonth}
                        onChange={(e) => setAdminMonth(e.target.value)}
                      >
                        <option value="">Any</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                          <option key={m} value={m}>
                            {monthName(m)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button type="submit" className="btn btn-primary">
                      Apply
                    </button>
                  </form>
                ) : null}

                <div className="table-responsive">
                  <table className="table table-hover role-table">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>
                          <span className="role-th">Sl&nbsp;No.</span>
                        </th>
                        {isAdmin ? (
                          <th style={{ minWidth: 200 }}>
                            <span className="role-th">Tenant</span>
                          </th>
                        ) : null}
                        <th style={{ minWidth: 140 }}>
                          <span className="role-th">Period</span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">Rent (₹)</span>
                        </th>
                        <th style={{ width: 130 }}>
                          <span className="role-th">Payment status</span>
                        </th>
                        {!isAdmin ? (
                          <th style={{ width: 110 }}>
                            <span className="role-th">Record</span>
                          </th>
                        ) : (
                          <th style={{ minWidth: 140 }}>
                            <span className="role-th">Receipts</span>
                          </th>
                        )}
                        <th>
                          <span className="role-th">Notes</span>
                        </th>
                        <th style={{ width: 160 }}>
                          <span className="role-th">Updated</span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">Action</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.length ? (
                        payments.map((row, idx) => {
                          const ps = statusBadgeStyle("payment", row.paymentStatus);
                          const rs = statusBadgeStyle("record", row.status);
                          const refs = Array.isArray(row.paymentReference)
                            ? row.paymentReference
                            : [];
                          return (
                            <tr key={row._id || idx}>
                              <td>
                                {(pagination.page - 1) * pagination.limit + idx + 1}
                              </td>
                              {isAdmin ? (
                                <td style={{ fontSize: 13 }}>{getTenantLabel(row)}</td>
                              ) : null}
                              <td>{formatPeriod(row.month, row.year)}</td>
                              <td>{formatNumber(row.rentAmount)}</td>
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    padding: "0.35rem 0.65rem",
                                    borderRadius: 999,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    background: ps.bg,
                                    color: ps.color,
                                    textTransform: "capitalize"
                                  }}
                                >
                                  {row.paymentStatus || "-"}
                                </span>
                              </td>
                              {isAdmin ? (
                                <td style={{ fontSize: 12 }}>
                                  {refs.length ? (
                                    <ul className="mb-0 ps-3">
                                      {refs.map((ref, ri) => {
                                        const href =
                                          ref.downloadUrl || ref.url || null;
                                        const label =
                                          ref.originalFileName ||
                                          `Receipt ${ri + 1}`;
                                        return (
                                          <li key={ref.s3Key || ri}>
                                            {href ? (
                                              <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                {label}
                                              </a>
                                            ) : (
                                              label
                                            )}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                              ) : (
                                <td>
                                  <span
                                    className="badge"
                                    style={{
                                      padding: "0.35rem 0.65rem",
                                      borderRadius: 999,
                                      fontWeight: 600,
                                      fontSize: 11,
                                      background: rs.bg,
                                      color: rs.color,
                                      textTransform: "capitalize"
                                    }}
                                  >
                                    {row.status || "-"}
                                  </span>
                                </td>
                              )}
                              <td className="text-muted" style={{ maxWidth: 220 }}>
                                {row.notes?.trim() ? row.notes : "—"}
                              </td>
                              <td className="text-muted" style={{ fontSize: 12 }}>
                                {formatDateTime(row.updatedAt || row.createdAt)}
                              </td>
                              <td>
                                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                  {!isAdmin && isPaymentPending(row) ? (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handlePay(row)}
                                    >
                                      Pay
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="role-action-btn role-action-view"
                                      title="View"
                                      onClick={() => handleView(row)}
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={isAdmin ? 9 : 8} className="text-center text-muted">
                            {loading ? "Loading…" : "No payment records"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="role-table-footer">
                  <div
                    className="d-flex justify-content-between align-items-center flex-wrap"
                    style={{ gap: 10 }}
                  >
                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        Show:
                      </span>
                      <select
                        className="form-control"
                        style={{ width: 90 }}
                        value={pagination.limit}
                        onChange={(e) => handleLimitChange(Number(e.target.value))}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        of {formatNumber(totalEntries)} entries
                      </span>
                    </div>

                    <div className="d-flex align-items-center" style={{ gap: 4 }}>
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page <= 1}
                        onClick={() => handlePageChange(1)}
                        style={{ minWidth: 32, height: 32, padding: "4px 8px", fontSize: 12 }}
                      >
                        &laquo;
                      </button>
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page <= 1}
                        onClick={() => handlePageChange(pagination.page - 1)}
                        style={{ minWidth: 32, height: 32, padding: "4px 8px", fontSize: 12 }}
                      >
                        &lsaquo;
                      </button>

                      {pageNumbers.map((pageNum, index) => {
                        if (pageNum === "...") {
                          return (
                            <span
                              key={`ellipsis-${index}`}
                              style={{
                                padding: "4px 8px",
                                fontSize: 12,
                                color: "#6c757d",
                                minWidth: 32,
                                textAlign: "center",
                                display: "inline-block"
                              }}
                            >
                              ...
                            </span>
                          );
                        }
                        const isActive = pageNum === pagination.page;
                        return (
                          <button
                            key={pageNum}
                            type="button"
                            className={`btn ${isActive ? "btn-primary" : "btn-light"}`}
                            onClick={() => handlePageChange(pageNum)}
                            style={{
                              minWidth: 32,
                              height: 32,
                              padding: "4px 8px",
                              fontSize: 12,
                              fontWeight: isActive ? 600 : 400
                            }}
                          >
                            {pageNum}
                          </button>
                        );
                      })}

                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => handlePageChange(pagination.page + 1)}
                        style={{ minWidth: 32, height: 32, padding: "4px 8px", fontSize: 12 }}
                      >
                        &rsaquo;
                      </button>
                      <button
                        type="button"
                        className="btn btn-light"
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => handlePageChange(pagination.pages)}
                        style={{ minWidth: 32, height: 32, padding: "4px 8px", fontSize: 12 }}
                      >
                        &raquo;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {payModalRow ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="payment-proof-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !submittingProof) closePayModal();
          }}
        >
          <div
            className="modal-content"
            style={{ minHeight: "auto", maxWidth: 520, width: "100%" }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 className="modal-title" id="payment-proof-title">
                Submit payment proof
              </h2>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={closePayModal}
                disabled={submittingProof}
              >
                &times;
              </button>
            </div>
            <form onSubmit={submitRentPaymentProof}>
              <div className="modal-body">
                <p style={{ margin: "0 0 16px", color: "#6c757d", fontSize: 14 }}>
                  <strong>{formatPeriod(payModalRow.month, payModalRow.year)}</strong>
                  {" · "}
                  <strong>₹ {formatNumber(payModalRow.rentAmount)}</strong>
                </p>
                <div className="form-group">
                  <label className="form-label">
                    Payment screenshot <span className="required-star">*</span>
                  </label>
                  <input
                    key={payModalRow._id}
                    type="file"
                    className="form-control"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={onProofFileChange}
                    disabled={submittingProof}
                  />
                  <small className="text-muted">JPEG, PNG, WebP, or GIF — max 8 MB</small>
                </div>
                {proofPreviewUrl ? (
                  <div className="form-group" style={{ marginTop: 12 }}>
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: 8,
                        background: "#f9fafb",
                        textAlign: "center"
                      }}
                    >
                      <img
                        src={proofPreviewUrl}
                        alt="Receipt preview"
                        style={{ maxWidth: "100%", maxHeight: 220, objectFit: "contain" }}
                      />
                    </div>
                    {proofFile ? (
                      <small className="text-muted d-block mt-1">
                        {proofFile.name} · {proofFile.type || "image/*"}
                      </small>
                    ) : null}
                  </div>
                ) : null}
                <div className="form-group">
                  <label className="form-label">Notes (optional)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="e.g. Paid via GPay"
                    value={proofNotes}
                    onChange={(e) => setProofNotes(e.target.value)}
                    disabled={submittingProof}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ justifyContent: "flex-end", marginTop: 0 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closePayModal}
                  disabled={submittingProof}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submittingProof}>
                  {submittingProof ? "Uploading…" : "Submit proof"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Payment;
